/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import api from '../../services/api';
import List from '../../components/Crud/List';
import { useAuth } from '../../contexts/auth';
import { getEntityName, getEntityRoute } from '../../enums/EntityEnum';
import { getAuditLogName } from '../../enums/AuditLogActionEnum';
import AuditLogsFilter, { AuditLogFilterFields } from './AuditLogsFilter';
import {
  getLastFilterByKey,
  upsertFilterToKey,
} from '../../services/filterService';
import CustomModal from '../../components/CustomModal/CustomModal';

type UserModel = {
  id: number;
  role: string;
  name: string;
  email: string;
};

type AuditLogModel = {
  id: number;
  action: string;
  entity: string;
  entityId: number;
  data?: string[];
  user: UserModel;
  createdAt: string;
};

export default function AuditLogsIndex() {
  const pageKey = 'audit-logs';
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([] as AuditLogModel[]);
  const [auditLogSelected, setAuditLogSelected] = useState(
    null as unknown as AuditLogModel
  );
  const [filterFields, setFilterFields] = useState<AuditLogFilterFields>(
    null as unknown as AuditLogFilterFields
  );

  const {
    user: { organization },
  } = useAuth();

  async function getAuditLogs(data: AuditLogFilterFields) {
    if (organization) {
      setIsLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/audit-logs`,
        {
          params: data,
        }
      );
      setAuditLogs(response.data.data);
      setIsLoading(false);
    }
  }

  const onFilterSubmit = (data: AuditLogFilterFields) => {
    const filter = new AuditLogFilterFields(data);
    upsertFilterToKey(pageKey, filter);
    setFilterFields(filter);
    getAuditLogs(data);
  };

  useEffect(() => {
    const filters = getLastFilterByKey(pageKey, AuditLogFilterFields);
    setFilterFields(filters);
    getAuditLogs(filters);
  }, [organization]);

  function formatCell(
    top: JSX.Element | string,
    bottom: JSX.Element | string = ''
  ) {
    return (
      <div className='d-flex flex-column'>
        <div>{top}</div>
        {bottom ? (
          <div>
            <small className='text-muted'>{bottom}</small>
          </div>
        ) : undefined}
      </div>
    );
  }

  return (
    <>
      <List
        isLoading={isLoading}
        title='Logs'
        newItemUrl=''
        withNewAction={false}
        filter={
          <AuditLogsFilter
            pageKey={pageKey}
            filterFields={filterFields}
            onSubmit={onFilterSubmit}
          />
        }
      >
        <table className='table table-hover'>
          <thead>
            <tr>
              <th scope='col'>#</th>
              <th scope='col'>Ação</th>
              <th scope='col'>Data</th>
              <th scope='col' className='text-end'>
                Id
              </th>
              <th scope='col'>Entidade</th>
              <th scope='col'>Responsável</th>
              <th scope='col'>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map((i) => {
              const createdAt = new Date(i.createdAt);
              return (
                <tr key={i.id}>
                  <th scope='row'>{i.id}</th>
                  <td>{getAuditLogName(i.action)}</td>
                  <td>
                    {formatCell(
                      createdAt.toLocaleDateString(),
                      createdAt.toLocaleTimeString()
                    )}
                  </td>
                  <td className='text-end'>
                    <Link to={`/${getEntityRoute(i.entity, i.entityId)}`}>
                      #&nbsp;{i.entityId}
                    </Link>
                  </td>
                  <td>{getEntityName(i.entity)}</td>
                  <td>{i.user.name}</td>
                  <td>
                    <FaEye
                      cursor='pointer'
                      title='Visualizar Alterações'
                      onClick={() => {
                        setAuditLogSelected(i);
                        setIsOpenModal(true);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </List>
      {auditLogSelected && (
        <CustomModal
          modalTitle={getEntityName(auditLogSelected.entity)}
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
        >
          {auditLogSelected.data?.map((d) => {
            return <span key={auditLogSelected.id + d}>{d}</span>;
          })}
        </CustomModal>
      )}
    </>
  );
}
